import React, { ChangeEvent, useEffect, useState, useRef } from 'react'
import { noop } from 'lodash'
import { Icon } from '@pff-consumer/web-ui/components/icon/icon'
import { useFeatureFlags } from '@pff-consumer/feature-flags'
import { useAnalytics } from '@pff-consumer/analytics'
import { SVGGroup } from '@pff-consumer/utils'
import { ProgressPane } from '@pff-consumer/feature-fantasy/web/lib/progress-pane/progress-pane.web'
import { CurrentProgressState } from '@pff-consumer/schema'
import { useCreateUserAccountMutation } from '@pff-consumer/data-provider'
import { useRouter } from 'next/router'
import ReCAPTCHA from 'react-google-recaptcha'
import { Button } from '../common/button/button'
import { useStyles } from './create-account.styles'
import { createAccountformValidators, CreateAccountValidation, formValidators } from './create-account-validation'
import { CTALocation } from '../../constants/constants'
import { Routes } from '../../utils/routes'
import { CreateAccountSuccess } from '../create-account-success/create-account-success'
import { CreateAccountError } from '../create-account-error/create-account-error'
import { useRedirectTo } from '../common/hooks/useRedirectTo'

// This is added because safari has a key icon, so in order to apply CSS to that, we need to define this
declare global {
  interface Window {
    // This only exists in safari browsers
    safari?: any
  }
}

// TODO: Fix so we let the HTML form control the state
export const CreateAccount = () => {
  const [createAccountValidation, setCreateAccountValidation] = useState<CreateAccountValidation>(formValidators)
  const [email, setEmail] = useState<string>('')
  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true)
  const [isSafari, setSafari] = useState(false)
  const [password, setPassword] = useState<string>('')
  const [shouldSubscribeToNewsletter, setShouldSubscribeToNewsletter] = useState<boolean>(true)
  const [ignoreError, setIgnoreError] = useState<boolean>(false)
  const [createAccount, { error, isLoading: loading, isSuccess: success }] = useCreateUserAccountMutation()
  const { sendAnalyticsEvent } = useAnalytics()
  const { getFeatureFlag } = useFeatureFlags()
  const styles = useStyles({ isSafari })
  const router = useRouter()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { redirectTo } = useRedirectTo({ router })

  useEffect(() => {
    setSafari(window && window?.safari !== undefined)
  }, [])

  const shouldDisplayGoogleSignIn = getFeatureFlag<boolean>('enable-google-social-sign-in', true)
  const shouldDisplayAppleSignIn = getFeatureFlag<boolean>('enable-apple-social-sign-in', true)
  const shouldDisplayFacebookSignIn = getFeatureFlag<boolean>('enable-facebook-sign-in', true)

  const processCreateAccount = async (recaptchaToken: string) => {
    const createAccountForm = createAccountformValidators(email, password)
    if (createAccountForm.validEmail || createAccountForm.validPassword) {
      setCreateAccountValidation(() => ({ ...createAccountForm }))
      sendAnalyticsEvent('CTA Clicked', {
        ctaName: 'Create Account',
        ctaLocation: CTALocation.CREATE_FREE_ACCOUNT,
      })
      createAccount({ email, password, shouldSubscribeToNewsletter, recaptchaToken })
      setIgnoreError(false)
    }
  }

  const processCreateAccountWithRecaptcha = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (recaptchaRef.current) {
      const token = await recaptchaRef.current.executeAsync()
      if (token) {
        await processCreateAccount(token)
      }
    }

    return null
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    setEmail(e.target.value)
    const createAccountForm = createAccountformValidators(e.target.value, password)
    setCreateAccountValidation(() => ({ ...createAccountForm }))
  }

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const createAccountForm = createAccountformValidators(email, e.target.value)
    setCreateAccountValidation(() => ({ ...createAccountForm }))
    setPassword(e.target.value)
  }

  const handleSubscribedChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setShouldSubscribeToNewsletter(e.target.checked)
  }

  if (loading) {
    return (
      <div className={styles.createAccountContainer}>
        <ProgressPane
          state={CurrentProgressState.IN_PROGRESS}
          header='Creating Your Account.'
          message='In the red zone going for it on 4th and 1.'
        />
      </div>
    )
  }

  if (error && !ignoreError) {
    return (
      <CreateAccountError
        onClick={() => {
          setIgnoreError(true)
          router.push({ query: {} }, undefined, { shallow: true })
        }}
      />
    )
  }

  if (success) {
    return <CreateAccountSuccess />
  }

  return (
    <div className={styles.createAccountContainer}>
      <form
        onSubmit={processCreateAccountWithRecaptcha}
        className={styles.mainContainer}
      >
        <h1 className={styles.heading}>Create a FREE Account</h1>
        <h2 className={styles.headingText}>Create an account to sync your fantasy league for FREE today.</h2>
        <div>
          <div className={styles.textFieldContainer}>
            <label className={styles.textFieldLabel}>
              Email <span className={styles.mandatoryIndicator}> *</span>
            </label>
            <input
              type='email'
              placeholder='Email'
              name='email'
              className={createAccountValidation.validEmail ? styles.email : styles.invalidEmail}
              onBlur={handleEmailChange}
            />
            {!createAccountValidation.validEmail && (
              <div
                className={styles.errorMessage}
                data-testid='error-message'
              >
                <Icon
                  name='field-error'
                  group={SVGGroup.Icons}
                  width={12}
                  height={12}
                />
                {createAccountValidation.emailErrorMessage}
              </div>
            )}
          </div>
          <div className={styles.textFieldContainer}>
            <label className={styles.textFieldLabel}>
              Password <span className={styles.mandatoryIndicator}> *</span>
            </label>
            <input
              type={isPasswordHidden ? 'password' : 'text'}
              placeholder='Password'
              name='password'
              className={styles.password}
              onChange={handlePasswordChange}
            />
            <button
              className={styles.passwordIconContainer}
              type='button'
              onClick={() => setIsPasswordHidden(!isPasswordHidden)}
            >
              <Icon
                name={isPasswordHidden ? 'password-hidden' : 'password-shown'}
                group={SVGGroup.Icons}
                width={16}
                height={16}
              />
            </button>
          </div>
          <div
            className={
              createAccountValidation.validPassword
                ? styles.newsletterCheckboxContainerSuccess
                : styles.newsletterCheckboxContainer
            }
          >
            <Icon
              name={createAccountValidation.validPassword ? 'circle-checkmark-green' : 'circle-checkmark-grey'}
              group={SVGGroup.Icons}
              height={16}
              width={16}
            />
            <label
              className={styles.newsletterCheckboxLabel}
              htmlFor='newsletterCheckbox'
            >
              Password must be min 8 characters
            </label>
          </div>
          <div className={styles.newsletterCheckboxContainer}>
            <input
              type='checkbox'
              className={styles.newsletterCheckbox}
              id='newsletterCheckbox'
              onChange={handleSubscribedChange}
              defaultChecked
            />
            <label
              className={styles.newsletterCheckboxLabel}
              htmlFor='newsletterCheckbox'
            >
              Sign me up to receive exclusive promotions, news, and other features from PFF via email
            </label>
          </div>
          <Button
            className={styles.createAccountButton}
            text='Create account'
            role='button'
            variant='Primary'
            type='submit'
            disabled={!createAccountValidation.isValid}
          />
        </div>
        <div className={styles.socialMediaOptionContainer}>
          <hr className={styles.optionLine} />
          <span className={styles.socialMediaOptionText}>Or continue with</span>
          <hr className={styles.optionLine} />
        </div>
        <div className={styles.socialMediaButton}>
          {shouldDisplayAppleSignIn && (
            <Button
              variant='SocialMedia'
              onClick={noop}
              data-testid='apple-sign'
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='apple-blue'
              text='Apple'
            />
          )}

          {shouldDisplayGoogleSignIn && (
            <Button
              variant='SocialMedia'
              onClick={noop}
              data-testid='google-sign'
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='google-blue'
              text='Google'
            />
          )}
          {shouldDisplayFacebookSignIn && (
            <Button
              variant='SocialMedia'
              data-testid='facebook-sign'
              onClick={noop}
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='facebook-blue'
              text='Facebook'
            />
          )}
        </div>
        <div className={styles.signInLink}>
          Already have an account?{' '}
          <button
            type='button'
            onClick={() => {
              window.location.href = `${Routes.Login}?redirect_to=${redirectTo}`
            }}
          >
            Sign In
          </button>
        </div>
        <ReCAPTCHA
          ref={recaptchaRef}
          size='invisible'
          sitekey={process.env.RECAPTCHA_SITE_KEY!}
        />
      </form>
    </div>
  )
}
